import { useState } from 'react';

import NiceModal, { NiceModalHocProps, useModal } from '@ebay/nice-modal-react';
import { Modal } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';

import { Button, IconButton, Input, Typography } from '@app/components';
import { DeleteIcon } from '@app/assets';
import { clsxm } from '@app/styles/clsxm';
import { maybeRenderError } from '@app/utils/maybeRenderError';
import { QrCodeRiskLogApi } from '@app/domain/qr-code-risk-log/api/QrCodeRiskLogApi';

const QUERY_KEY = 'admin-qr-risk-log-whitelist-domains';

/**
 * Danny's own list, editable from the risk-log screen (2026-09-02) - a QR-code destination
 * matching one of these (exact host or subdomain) never gets flagged would_block, regardless
 * of what Web Risk or any other check says. Built after a real destination (Padlet) kept
 * getting flagged during his first week of manual review.
 */
export const RiskWhitelistDomainsDialog: React.FC<NiceModalHocProps> = NiceModal.create(() => {
  const modal = useModal();
  const queryClient = useQueryClient();
  const [newDomain, setNewDomain] = useState('');
  const [removingId, setRemovingId] = useState<string | undefined>();

  const { data: domains, isFetching } = useQuery([QUERY_KEY], () => QrCodeRiskLogApi.adminGetWhitelistDomains());

  const addMutation = useMutation({
    mutationFn: (domain: string) => QrCodeRiskLogApi.adminAddWhitelistDomain(domain),
    onSuccess: () => {
      setNewDomain('');
      toast.success('Domain whitelisted.');
      queryClient.invalidateQueries(QUERY_KEY);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => QrCodeRiskLogApi.adminRemoveWhitelistDomain(id),
    onMutate: (id: string) => setRemovingId(id),
    onSuccess: () => {
      toast.success('Domain removed.');
      queryClient.invalidateQueries(QUERY_KEY);
    },
    onSettled: () => setRemovingId(undefined),
  });

  const closeDialog = () => modal.remove();

  const submitNewDomain = () => {
    if (!newDomain.trim()) {
      return;
    }
    addMutation.mutate(newDomain.trim());
  };

  return (
    <Modal open={modal.visible} onClose={closeDialog} onClick={(e) => e.stopPropagation()}>
      <div
        className={clsxm(
          'absolute top-[50%] left-[50%] w-[440px] max-w-[calc(100%-24px)] -translate-x-[50%] -translate-y-[50%]',
          'overflow-hidden rounded-3xl bg-white px-8 py-6'
        )}
      >
        <Typography variant="l" className="font-bold">
          Whitelisted domains
        </Typography>
        <Typography variant="xs" className="mt-1 text-gray-500">
          A destination matching one of these (exact host or subdomain) is never flagged, no matter what Web Risk or any
          other check finds.
        </Typography>

        <div className="mt-4 flex gap-2">
          <Input
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitNewDomain();
              }
            }}
            placeholder="e.g. padlet.com"
            withBG
          />
          <Button onClick={submitNewDomain} loading={addMutation.isLoading} variant="contained" color="primary">
            Add
          </Button>
        </div>
        {maybeRenderError(addMutation.error as any)}

        <div className="mt-4 max-h-[320px] space-y-1 overflow-y-auto">
          {isFetching && !domains && <Typography className="text-gray-400">Loading…</Typography>}
          {!isFetching && domains?.length === 0 && (
            <Typography className="text-gray-400">No domains whitelisted yet.</Typography>
          )}
          {domains?.map((entry) => (
            <div key={entry.id} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
              <Typography className="break-all">{entry.domain}</Typography>
              <IconButton
                size="small"
                loading={removingId === entry.id}
                onClick={() => entry.id && removeMutation.mutate(entry.id)}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={closeDialog} variant="outlined" color="secondary">
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
});
